import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Loader2, ArrowLeft } from 'lucide-react'
import toast from 'react-hot-toast'
import { showSaveSuccess, showSaveError, showValidationError, showLoading, closeSwal, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { InvoiceMultiSelect } from '@/components/ui/invoice-multiselect'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { paymentsService } from '@/services/paymentsService'
import { billsService } from '@/services/billsService'
import {
  PaymentVm,
  BillVendor,
  PaymentAccount,
  VendorBill,
  VendorAdvanceForPayment,
} from '@/api/types'

export const PaymentsDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const isEditMode = id !== 'new' && id !== undefined
  const branchId = user?.selectedBranch?.branchId

  const [payment, setPayment] = useState<PaymentVm>({
    paymentId: 0,
    paymentDate: new Date().toISOString().split('T')[0],
    paymentNo: '',
    amount: 0,
    vendorId: 0,
    advanceId: 0,
    refNo: '',
    paymentType: '',
    recipientAccount: 0,
    destinationAccount: 0,
    branchId: branchId || 0,
    note: '',
    ipAddress: '',
    billIds: [], // Now array of PaymentBillLine objects
  })

  // Additional form fields for display
  const [selectedAdvanceAmount, setSelectedAdvanceAmount] = useState<number>(0)
  const [, setPreviousPaymentAmount] = useState<number>(0)
  const [, setAdvanceLinesAmount] = useState<number>(0)
  
  // Bill table data - each row represents a bill with its payment details
  interface BillTableRow {
    billId: number
    billNo: string
    payStatus: string
    paidAmount: number
    remainingAmount: number
    amount: number // Amount to pay for this bill
    lineId?: number // For edit mode - payment line ID
  }
  const [billTableData, setBillTableData] = useState<BillTableRow[]>([])

  const [vendors, setVendors] = useState<BillVendor[]>([])
  const [accounts, setAccounts] = useState<PaymentAccount[]>([])
  const [bills, setBills] = useState<VendorBill[]>([])
  const [advances, setAdvances] = useState<VendorAdvanceForPayment[]>([])
  const [selectedVendor, setSelectedVendor] = useState<BillVendor | null>(null)
  const [selectedBillIds, setSelectedBillIds] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // Payment type options
  const paymentTypeOptions = [
    { value: 'Bank', label: t('payments.bank') },
    { value: 'Cash', label: t('payments.cash') },
  ]

  // Load vendors and accounts
  useEffect(() => {
    if (!branchId) return

    const loadLookups = async () => {
      try {
        setLoading(true)
        const [vendorsData, accountsData] = await Promise.all([
          billsService.getAllVendors(branchId),
          paymentsService.getAllAccounts(branchId),
        ])

        setVendors(vendorsData || [])
        setAccounts(accountsData || [])
      } catch (error: any) {
        await showError('Error', error.response?.data?.message || t('payments.failedToLoadData'))
      } finally {
        setLoading(false)
      }
    }

    loadLookups()
  }, [branchId])

  // Load bills when vendor is selected
  useEffect(() => {
    if (!selectedVendor?.vendorId) {
      setBills([])
      setSelectedBillIds([])
      return
    }

    const loadBills = async () => {
      try {
        if (!selectedVendor?.vendorId) return
        const billsData = await paymentsService.getVendorsBills(selectedVendor.vendorId!)
        setBills(billsData || [])
      } catch (error: any) {
        console.error('Failed to load bills', error)
        await showError('Error', error.response?.data?.message || t('payments.failedToLoadBills'))
      }
    }

    loadBills()
  }, [selectedVendor])

  // Update bill table data when selected bills change
  useEffect(() => {
    if (selectedBillIds.length === 0) {
      setBillTableData([])
      setPayment(prev => ({ ...prev, billIds: [], amount: 0 }))
      return
    }

    const selected = bills.filter(bill => selectedBillIds.includes(String(bill.billId)))

    if (selected.length > 0) {
      if (isEditMode) {
        // In edit mode, use bill data directly (payment lines data is already loaded and processed)
        // When user changes selection in edit mode, show current bill state
        const tableData: BillTableRow[] = selected.map(bill => {
          const remaining = bill.remainingAmount || 0
          const paid = bill.paidAmount || 0
          // If remaining amount is 0, show "Paid", otherwise use bill payStatus
          const payStatus = remaining === 0 ? 'Paid' : (paid === 0 ? 'Not Paid' : (bill.payStatus || 'Partially Paid'))
          
          return {
            billId: bill.billId,
            billNo: bill.billNo || '',
            payStatus: payStatus,
            paidAmount: paid,
            remainingAmount: remaining,
            amount: remaining, // Default to remaining amount
            lineId: 0, // Will be set from payment lines
          }
        })
        setBillTableData(tableData)
      } else {
        // For new payments, create table data from selected bills
        const tableData: BillTableRow[] = selected.map(bill => {
          const remaining = bill.remainingAmount || 0
          const paid = bill.paidAmount || 0
          // If remaining amount is 0, show "Paid", otherwise use bill payStatus
          const payStatus = remaining === 0 ? 'Paid' : (paid === 0 ? 'Not Paid' : (bill.payStatus || 'Partially Paid'))
          
          return {
            billId: bill.billId,
            billNo: bill.billNo || '',
            payStatus: payStatus,
            paidAmount: paid,
            remainingAmount: remaining,
            amount: remaining, // Default to remaining amount
          }
        })
        setBillTableData(tableData)
      }
    }
  }, [selectedBillIds, bills, isEditMode])

  // Calculate total amount when bill table data or advance changes
  useEffect(() => {
    if (billTableData.length === 0) {
      setPayment(prev => ({ ...prev, amount: 0 }))
      return
    }

    // Calculate total amount from bill amounts
    const totalAmount = billTableData.reduce((sum, row) => sum + (row.amount || 0), 0)
    
    // Auto-calculate amount based on advance
    if (selectedAdvanceAmount > 0) {
      const calculatedAmount = totalAmount - selectedAdvanceAmount
      setPayment(prev => ({
        ...prev,
        amount: calculatedAmount > 0 ? calculatedAmount : 0,
      }))
    } else {
      setPayment(prev => ({
        ...prev,
        amount: totalAmount,
      }))
    }
  }, [billTableData, selectedAdvanceAmount])

  // Load advances when vendor is selected
  useEffect(() => {
    if (!selectedVendor?.vendorId) {
      setAdvances([])
      return
    }

    const loadAdvances = async () => {
      try {
        if (!branchId) return
        // Get all advances for the branch, then filter by vendor if needed
        const advancesData = await paymentsService.getAllAdvance(branchId)
        // Filter advances for this vendor (if the API doesn't filter by vendor)
        // Note: This assumes advances have a vendorId field. Adjust based on actual API response.
        setAdvances(advancesData || [])
      } catch (error: any) {
        console.error('Failed to load advances', error)
        await showError('Error', error.response?.data?.message || t('payments.failedToLoadAdvances'))
      }
    }

    loadAdvances()
  }, [selectedVendor, branchId])

  // Load payment data in edit mode
  useEffect(() => {
    if (!isEditMode || !branchId || !id) return
    if (vendors.length === 0) {
      // Wait for vendors to load
      return
    }

    const loadPayment = async () => {
      try {
        setLoading(true)
        const paymentId = Number(id)
        const paymentData = await paymentsService.getPaymentById(branchId, paymentId)

        // Store previous payment amount for calculations
        const previousAmount = paymentData.amount || 0
        setPreviousPaymentAmount(previousAmount)

        // Load advance lines for this payment
        let advanceLinesTotal = 0
        if (paymentData.advanceId) {
          try {
            const advanceLines = await paymentsService.getVendorAdvanceLineByAdvanceId(paymentData.advanceId)
            advanceLinesTotal = advanceLines.reduce((sum: number, line: any) => {
              return sum + (line.amount || 0)
            }, 0)
            setAdvanceLinesAmount(advanceLinesTotal)
          } catch (error) {
            setAdvanceLinesAmount(0)
          }
        }

        // Find and set vendor FIRST (like receipts sets customer first)
        // The useEffect will run but will be skipped in edit mode, and bills will be loaded manually anyway
        const vendor = vendors.find(v => v.vendorId === paymentData.vendorId)
        if (vendor) {
          setSelectedVendor(vendor)
        }

        // Load payment lines to get all bill IDs (for edit mode, use payment lines directly)
        let paymentLines: any[] = []
        try {
          paymentLines = await paymentsService.getPaymentLinesByPaymentId(paymentId)
          console.log('🔵 [PaymentsDetail] Payment lines loaded:', paymentLines)
        } catch (error) {
          console.warn('Failed to load payment lines', error)
        }

        // In edit mode: Extract bill IDs directly from payment lines and set them
        // No need to match with available bills - just use payment lines data
        if (paymentLines.length > 0) {
          // Extract bill IDs directly from payment lines
          const billIdsFromLines = paymentLines
            .map(line => line.billId)
            .filter((id): id is number => id !== null && id !== undefined)
          
          console.log('🔵 [PaymentsDetail] Bill IDs from payment lines:', billIdsFromLines)
          
          if (billIdsFromLines.length > 0) {
            // Set selected bill IDs directly from payment lines
            const billIdsToSet = billIdsFromLines.map(id => String(id))
            console.log('🔵 [PaymentsDetail] Setting selectedBillIds directly from payment lines:', billIdsToSet)
            setSelectedBillIds(billIdsToSet)
            
            // Load only the selected bills for dropdown display (not all vendor bills)
            if (paymentData.vendorId && billIdsFromLines.length > 0) {
              try {
                // Load all vendor bills first to get bill details
                const allBillsData = await paymentsService.getVendorsBills(paymentData.vendorId)
                // Filter to only selected bills for dropdown
                const selectedBillsForDisplay = allBillsData.filter(bill => 
                  billIdsFromLines.includes(bill.billId)
                )
                // Set only selected bills in state (so dropdown only shows selected bills)
                setBills(selectedBillsForDisplay)
                
                // Populate table data from payment lines
                // Use payment line data directly, fallback to bill data if needed
                const tableData: BillTableRow[] = paymentLines.map((paymentLine: any) => {
                  const billId = paymentLine.billId
                  const lineId = paymentLine.lineId || 0
                  // Find bill details if available
                  const billDetails = selectedBillsForDisplay.find(b => b.billId === billId)
                  
                  // Get amounts from payment line
                  const paidAmountFromLine = paymentLine.paidAmount || paymentLine.amount || 0
                  
                  // For edit mode, calculate remaining amount
                  // If we have bill details, use them; otherwise estimate from payment line
                  let calculatedPaidAmount = 0
                  let calculatedRemainingAmount = 0
                  
                  if (billDetails) {
                    // Calculate: reverse the payment effect
                    const totalRemainingBeforePayment = selectedBillsForDisplay.reduce((sum, b) => sum + (b.remainingAmount || 0), 0)
                    const billRemainingRatio = totalRemainingBeforePayment > 0
                      ? (billDetails.remainingAmount || 0) / totalRemainingBeforePayment
                      : 1 / selectedBillsForDisplay.length
                    const billAdvanceAmount = advanceLinesTotal * billRemainingRatio
                    calculatedPaidAmount = (billDetails.paidAmount || 0) - paidAmountFromLine - billAdvanceAmount
                    calculatedRemainingAmount = (billDetails.remainingAmount || 0) + paidAmountFromLine + billAdvanceAmount
                  } else {
                    // Fallback: use payment line data directly
                    calculatedPaidAmount = paidAmountFromLine
                    calculatedRemainingAmount = 0 // Assume fully paid if we don't have bill details
                  }
                  
                  return {
                    billId: billId,
                    billNo: billDetails?.billNo || paymentLine.billNo || `Bill ${billId}`,
                    payStatus: calculatedRemainingAmount === 0 ? 'Paid' : (calculatedPaidAmount === 0 ? 'Not Paid' : 'Partially Paid'),
                    paidAmount: calculatedPaidAmount > 0 ? calculatedPaidAmount : 0,
                    remainingAmount: calculatedRemainingAmount,
                    amount: paidAmountFromLine, // Use amount from payment line
                    lineId: lineId, // Store lineId for update
                  }
                })
                
                setBillTableData(tableData)
                
                // Calculate total remaining amount for auto-calculating payment amount
                const totalRemainingAmount = tableData.reduce((sum, row) => sum + row.remainingAmount, 0)
                
                // Auto-calculate amount based on advance
                if (selectedAdvanceAmount > 0) {
                  const calculatedAmount = totalRemainingAmount - selectedAdvanceAmount
                  setPayment(prev => ({
                    ...prev,
                    amount: calculatedAmount > 0 ? calculatedAmount : 0,
                  }))
                } else {
                  setPayment(prev => ({
                    ...prev,
                    amount: totalRemainingAmount,
                  }))
                }
              } catch (error) {
                console.warn('Failed to load bills for display:', error)
                // Still set selected bill IDs even if bill details fail to load
              }
            }
          }
        } else {
          // Fallback: if no payment lines, try old method with single billId
          if (paymentData.vendorId && paymentData.billId) {
            try {
              const billsData = await paymentsService.getVendorsBills(paymentData.vendorId)
              const bill = billsData.find(b => b.billId === paymentData.billId)
              if (bill) {
                setSelectedBillIds([String(bill.billId)])
                setBills([bill]) // Only set the selected bill
                
                const calculatedPaidAmount = (bill.paidAmount || 0) - previousAmount - advanceLinesTotal
                const calculatedRemainingAmount = (bill.remainingAmount || 0) + previousAmount + advanceLinesTotal
                
                const tableData: BillTableRow[] = [{
                  billId: bill.billId,
                  billNo: bill.billNo || '',
                  payStatus: calculatedRemainingAmount === 0 ? 'Paid' : (calculatedPaidAmount === 0 ? 'Not Paid' : 'Partially Paid'),
                  paidAmount: calculatedPaidAmount > 0 ? calculatedPaidAmount : 0,
                  remainingAmount: calculatedRemainingAmount,
                  amount: previousAmount, // Use previous amount
                }]
                setBillTableData(tableData)
              }
            } catch (error) {
              console.warn('Failed to load fallback bill:', error)
            }
          }
        }

        // Load advances for vendor (pass paymentId for edit mode)
        if (paymentData.vendorId) {
          try {
            const advancesData = await paymentsService.getAllAdvance(branchId)
            // Filter for vendor if needed
            setAdvances(advancesData || [])
            
            // Find selected advance
            if (paymentData.advanceId) {
              const advance = advancesData.find((adv: any) => (adv.id || adv.advanceId) === paymentData.advanceId)
              if (advance) {
                setSelectedAdvanceAmount(advance.amount || 0)
              }
            }
          } catch (error) {
            setAdvances([])
          }
        }

        // Convert date to ISO string format for API
        const paymentDateISO = paymentData.paymentDate 
          ? new Date(paymentData.paymentDate).toISOString()
          : new Date().toISOString()

        setPayment({
          paymentId: paymentData.paymentId || 0,
          paymentDate: paymentDateISO.split('T')[0], // For date input
          paymentNo: paymentData.paymentNo || '',
          amount: paymentData.amount || 0,
          vendorId: paymentData.vendorId || 0,
          advanceId: paymentData.advanceId || 0,
          refNo: paymentData.refNo || '',
          paymentType: paymentData.paymentType || '',
          recipientAccount: paymentData.recipientAccount || 0,
          destinationAccount: paymentData.destinationAccount || 0,
          branchId: paymentData.branchId || branchId || 0,
          note: paymentData.note || '',
          ipAddress: paymentData.ipAddress || '',
          billIds: [], // Will be populated from billTableData
        })
      } catch (error: any) {
        await showError('Error', error.message || t('payments.failedToLoadPayment'))
      } finally {
        setLoading(false)
      }
    }

    loadPayment()
  }, [isEditMode, branchId, id, vendors.length])

  // Handle vendor selection
  const handleVendorChange = (vendorId: string) => {
    const vendor = vendors.find(v => v.vendorId === Number(vendorId))
    if (vendor) {
      setSelectedVendor(vendor)
      // Reset bill and advance related fields
      setPayment(prev => ({
        ...prev,
        vendorId: vendor.vendorId || 0,
        billIds: [],
        advanceId: 0,
        amount: 0,
      }))
      setSelectedBillIds([])
      setBillTableData([])
      setSelectedAdvanceAmount(0)
    }
  }

  // Handle bill selection (multiselect)
  const handleBillChange = (billIds: string[]) => {
    setSelectedBillIds(billIds)
  }

  // Handle advance selection
  const handleAdvanceChange = (advanceId: string) => {
    if (!advanceId || advanceId === '') {
      setSelectedAdvanceAmount(0)
      setPayment(prev => ({
        ...prev,
        advanceId: 0,
      }))
      
      // Reset amount to total remaining amount if no advance
      if (billTableData.length > 0) {
        const totalRemaining = billTableData.reduce((sum, row) => sum + row.remainingAmount, 0)
        setPayment(prev => ({
          ...prev,
          amount: totalRemaining,
        }))
      }
      return
    }

    const advance = advances.find(adv => String(adv.id || adv.advanceId) === advanceId)
    if (advance) {
      const advanceAmount = advance.amount || 0
      setSelectedAdvanceAmount(advanceAmount)
      setPayment(prev => ({
        ...prev,
        advanceId: advance.id || advance.advanceId || 0,
      }))
      
      // Calculate amount based on advance and total remaining amount
      if (billTableData.length > 0) {
        const totalRemaining = billTableData.reduce((sum, row) => sum + row.remainingAmount, 0)
        const calculatedAmount = totalRemaining - advanceAmount
        setPayment(prev => ({
          ...prev,
          amount: calculatedAmount > 0 ? calculatedAmount : 0,
        }))
      }
    }
  }

  // Handle bill amount change in table
  const handleBillAmountChange = (billId: number, value: number) => {
    setBillTableData(prev => prev.map(row => {
      if (row.billId === billId) {
        // Validate amount doesn't exceed remaining amount
        const maxAmount = row.remainingAmount
        const newAmount = value > maxAmount ? maxAmount : (value < 0 ? 0 : value)
        return { ...row, amount: newAmount }
      }
      return row
    }))
  }

  // Handle total amount change with validation (for backward compatibility)
  const handleAmountChange = (value: number) => {
    if (billTableData.length === 0) {
      setPayment(prev => ({ ...prev, amount: value }))
      return
    }

    const totalRemaining = billTableData.reduce((sum, row) => sum + row.remainingAmount, 0)
    const maxAmount = selectedAdvanceAmount > 0 
      ? totalRemaining - selectedAdvanceAmount 
      : totalRemaining

    if (value <= maxAmount) {
      setPayment(prev => ({ ...prev, amount: value }))
    } else {
      toast.error(t('payments.amountCannotExceed', { amount: maxAmount.toFixed(2) }))
    }
  }

  // Get user IP address
  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      return '0.0.0.0'
    }
  }

  const handleSave = async () => {
    if (!branchId) {
      await showValidationError(t('payments.pleaseSelectBranch'))
      return
    }

    if (selectedBillIds.length === 0) {
      await showValidationError(t('payments.pleaseSelectBill'))
      return
    }

    if (!payment.paymentType) {
      await showValidationError(t('payments.pleaseSelectPaymentType'))
      return
    }

    // Amount validation: If advance is selected, amount can be 0. Otherwise, amount must be > 0
    const hasAdvance = payment.advanceId && payment.advanceId > 0
    if (!hasAdvance && (!payment.amount || payment.amount <= 0)) {
      await showValidationError(t('payments.pleaseEnterValidAmount'))
      return
    }

    if (!payment.paymentDate) {
      await showValidationError(t('payments.pleaseSelectPaymentDate'))
      return
    }

    // Validate amount based on advance
    const totalRemaining = billTableData.reduce((sum, row) => sum + row.remainingAmount, 0)
    if (selectedAdvanceAmount > 0) {
      // If advance is selected and amount is 0, allow it (advance covers the payment)
      if (payment.amount === 0) {
        // Amount 0 is allowed when advance is selected
      } else {
        // Only validate if amount is greater than 0
        const maxAmount = totalRemaining - selectedAdvanceAmount
        if (maxAmount > 0 && payment.amount > maxAmount) {
          await showValidationError(t('payments.amountCannotExceed', { amount: maxAmount.toFixed(2) }))
          return
        }
      }
    } else {
      // No advance selected, validate normally
      if (payment.amount > totalRemaining) {
        await showValidationError(t('payments.amountCannotExceed', { amount: totalRemaining.toFixed(2) }))
        return
      }
    }

    // Declare paymentToSave outside try block for error logging
    let paymentToSave: PaymentVm | null = null

    try {
      setSaving(true)
      
      // Show loading
      showLoading(
        isEditMode ? 'Updating Payment...' : 'Saving Payment...',
        'Please wait while we process your request'
      )

      // Get logged-in user's IP address (not server's IP)
      const ipAddress = await getUserIPAddress()

      // Convert date to ISO string format (YYYY-MM-DD -> ISO string)
      const dateISO = payment.paymentDate 
        ? new Date(payment.paymentDate + 'T00:00:00').toISOString()
        : new Date().toISOString()

      // Prepare billIds array with new structure
      const billIdsArray = billTableData.map(row => ({
        lineId: row.lineId || 0, // 0 for new, existing lineId for edit
        billId: row.billId,
        amount: row.amount || 0,
        isDeleted: 'false',
      }))

      // Prepare payload matching exact API structure
      paymentToSave = {
        paymentId: isEditMode ? (payment.paymentId || 0) : 0, // Always 0 for new payments
        paymentDate: dateISO, // ISO format
        paymentNo: payment.paymentNo || undefined, // Don't send empty string, let backend generate
        amount: payment.amount,
        vendorId: selectedVendor?.vendorId || 0,
        advanceId: payment.advanceId || 0,
        refNo: payment.refNo || '',
        paymentType: payment.paymentType,
        recipientAccount: payment.recipientAccount || 0,
        destinationAccount: payment.destinationAccount,
        branchId: branchId || 0,
        note: payment.note || '',
        ipAddress: ipAddress, // Set logged-in user's IP address
        billIds: billIdsArray, // New structure with lineId, billId, amount, isDeleted
      }

      // Enhanced console logging for better visibility
      console.group('🔵 PAYMENT SAVE PAYLOAD')
      console.log('Mode:', isEditMode ? '✏️ UPDATE' : '➕ INSERT')
      console.log('Endpoint:', isEditMode ? 'PUT /api/Payments/Update' : 'POST /api/Payments/Insert')
      console.table(paymentToSave)
      console.log('Full Payload JSON:')
      console.log(JSON.stringify(paymentToSave, null, 2))
      console.log('Bill Lines Array:', paymentToSave.billIds)
      console.log('Total Amount:', paymentToSave.amount)
      console.log('Selected Vendor ID:', selectedVendor?.vendorId)
      console.groupEnd()

      if (isEditMode) {
        await paymentsService.updatePayment(paymentToSave)
      } else {
        await paymentsService.insertPayment(paymentToSave)
      }

      // Close loading
      closeSwal()
      
      // Show success
      await showSaveSuccess(
        isEditMode,
        isEditMode ? t('payments.paymentUpdatedSuccess') : t('payments.paymentCreatedSuccess')
      )

      navigate('/purchases/payments')
    } catch (error: any) {
      // Enhanced error logging
      console.group('❌ PAYMENT SAVE ERROR')
      console.error('Error Type:', error.name)
      console.error('Error Message:', error.message)
      console.error('HTTP Status:', error.response?.status)
      console.error('Error Response Data:')
      console.error(error.response?.data)
      
      if (error.response?.data?.msg) {
        console.error('Backend Error Message:', error.response.data.msg)
      }
      
      if (error.response?.data?.errors) {
        console.error('Validation Errors:', error.response.data.errors)
      }
      
      if (paymentToSave) {
        console.error('Request Payload (that caused error):')
        console.error(JSON.stringify(paymentToSave, null, 2))
      } else {
        console.error('Request Payload: Could not reconstruct (error occurred before payload creation)')
      }
      console.groupEnd()
      
      // Close loading if still open
      closeSwal()
      
      // Show error
      await showSaveError(error, isEditMode)
    } finally {
      setSaving(false)
    }
  }

  const vendorOptions: SearchableSelectOption[] = vendors.map((vendor) => ({
    value: String(vendor.vendorId || 0),
    label: vendor.vendorNameEn || vendor.vendorNameAr || `Vendor ${vendor.vendorId}`,
  }))

  // Filter bills based on mode
  // In add new mode: exclude bills with remaining amount 0
  // In edit mode: only show already selected bills (regardless of remaining amount)
  // But if selectedBillIds is empty, show all bills (they will be filtered once selectedBillIds is set)
  const filteredBills = isEditMode
    ? (selectedBillIds.length > 0 
        ? bills.filter(bill => selectedBillIds.includes(String(bill.billId)))
        : bills) // Show all bills if selectedBillIds not set yet
    : bills.filter(bill => (bill.remainingAmount || 0) > 0)

  const billOptions: SearchableSelectOption[] = filteredBills.map((bill) => ({
    value: String(bill.billId || 0),
    label: bill.billNo || '',
    disabled: isEditMode, // Disable selection in edit mode
  }))

  const advanceOptions: SearchableSelectOption[] = advances.map((adv) => ({
    value: String(adv.id || adv.advanceId || 0),
    label: `${adv.advNo || `Advance ${adv.id || adv.advanceId}`} - Amount: ${adv.amount?.toFixed(2) || '0.00'}`,
  }))

  const accountOptions: SearchableSelectOption[] = accounts.map((acc) => {
    const accountCode = acc.accountCode || ''
    const accountName = acc.accountNameEn || acc.accountName || acc.accountNameAr || `Account ${acc.id}`
    const label = accountCode ? `${accountCode} (${accountName})` : accountName
    return {
      value: String(acc.id || acc.accountId || 0),
      label: label,
    }
  })

  if (loading) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader title={t('payments.title')} description={isEditMode ? t('payments.editPayment') : t('payments.createNew')} />
        <Card>
          <CardContent className="p-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('payments.title')}
        description={isEditMode ? t('payments.editPayment') : t('payments.createNew')}
        action={{
          label: t('payments.return'),
          onClick: () => navigate('/purchases/payments'),
          icon: ArrowLeft,
        }}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-6">
            {/* Vendor and Bill Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>
                  {t('payments.vendor')} <span className="text-destructive">*</span>
                </Label>
                <div className="max-w-[400px]">
                  <SearchableSelect
                    options={vendorOptions}
                    value={selectedVendor ? String(selectedVendor.vendorId) : ''}
                    onValueChange={handleVendorChange}
                    placeholder={t('payments.selectVendor')}
                    searchPlaceholder={t('payments.searchVendors')}
                    emptyMessage={t('payments.noVendorsFound')}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>
                  {t('payments.billRefNo')} <span className="text-destructive">*</span>
                </Label>
                <div className="max-w-[400px]">
                  <InvoiceMultiSelect
                    options={billOptions}
                    value={selectedBillIds}
                    onValueChange={isEditMode ? undefined : handleBillChange}
                    placeholder={t('payments.selectBill')}
                    searchPlaceholder={t('payments.searchBills')}
                    emptyMessage={t('payments.noBillsFound')}
                    disabled={!selectedVendor}
                  />
                </div>
              </div>
            </div>

            {/* Bill Details Table */}
            {billTableData.length > 0 && (
              <div className="space-y-2">
                <Label>{t('payments.billDetails') || 'Bill Details'}</Label>
                <Card>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('payments.billRefNo') || 'Bill Ref No'}</TableHead>
                            <TableHead>{t('payments.payStatus') || 'Pay Status'}</TableHead>
                            <TableHead className="text-right">{t('payments.paidAmount') || 'Paid Amount'}</TableHead>
                            <TableHead className="text-right">{t('payments.remainingAmount') || 'Remaining Amount'}</TableHead>
                            <TableHead className="text-right">{t('payments.amount') || 'Amount'}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {billTableData.map((row) => (
                            <TableRow key={row.billId}>
                              <TableCell className="font-medium">{row.billNo}</TableCell>
                              <TableCell>{row.payStatus}</TableCell>
                              <TableCell className="text-right">{row.paidAmount.toFixed(2)}</TableCell>
                              <TableCell className="text-right">{row.remainingAmount.toFixed(2)}</TableCell>
                              <TableCell className="text-right align-middle p-2">
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max={row.remainingAmount}
                                  value={row.amount || ''}
                                  onChange={(e) => handleBillAmountChange(row.billId, Number(e.target.value) || 0)}
                                  className="w-24 text-right h-8 ml-auto"
                                  placeholder="0.00"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Payment Info Section */}
            <div className="space-y-4">
              <CardHeader className="px-0 pt-0">
                <CardTitle>{t('payments.paymentInfo')}</CardTitle>
              </CardHeader>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('payments.paymentDate')}</Label>
                  <div className="max-w-[400px]">
                    <Input
                      type="date"
                      value={payment.paymentDate}
                      onChange={(e) => setPayment(prev => ({ ...prev, paymentDate: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('payments.paymentNo')}</Label>
                  <div className="max-w-[400px]">
                    <Input
                      value={payment.paymentNo || ''}
                      onChange={(e) => setPayment(prev => ({ ...prev, paymentNo: e.target.value }))}
                      placeholder={t('payments.enterPaymentNumber')}
                      disabled={isEditMode} // Disable in edit mode as it's usually auto-generated
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('payments.recipientAccount')}</Label>
                  <div className="max-w-[400px]">
                    <SearchableSelect
                      options={accountOptions}
                      value={payment.recipientAccount ? String(payment.recipientAccount) : ''}
                      onValueChange={(value) => setPayment(prev => ({ ...prev, recipientAccount: Number(value) }))}
                      placeholder={t('payments.selectRecipientAccount')}
                      searchPlaceholder={t('payments.searchAccounts')}
                      emptyMessage={t('payments.noAccountsFound')}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('payments.advance')}</Label>
                  <div className="max-w-[400px]">
                    <SearchableSelect
                      options={advanceOptions}
                      value={payment.advanceId ? String(payment.advanceId) : ''}
                      onValueChange={handleAdvanceChange}
                      placeholder={t('payments.selectAdvance')}
                      searchPlaceholder={t('payments.searchAdvances')}
                      emptyMessage={t('payments.noAdvancesFound')}
                      disabled={!selectedVendor}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('payments.amount')}</Label>
                  <div className="max-w-[400px]">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={payment.amount || ''}
                      onChange={(e) => handleAmountChange(Number(e.target.value) || 0)}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('payments.paymentType')}</Label>
                  <div className="max-w-[400px]">
                    <Select
                      value={payment.paymentType}
                      onValueChange={(value) => setPayment(prev => ({ ...prev, paymentType: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('payments.selectPaymentType')} />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('payments.refNo')}</Label>
                  <div className="max-w-[400px]">
                    <Input
                      value={payment.refNo || ''}
                      onChange={(e) => setPayment(prev => ({ ...prev, refNo: e.target.value }))}
                      placeholder={t('payments.enterReferenceNumber')}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Note Section */}
            <div className="space-y-2">
              <Label>{t('payments.note')}</Label>
              <Textarea
                value={payment.note || ''}
                onChange={(e) => setPayment(prev => ({ ...prev, note: e.target.value }))}
                placeholder={t('payments.enterNotes')}
                rows={4}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form Actions */}
      <div className="flex flex-col sm:flex-row justify-end gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/purchases/payments')}
          className="w-full sm:w-auto"
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full sm:w-auto min-w-[150px]"
        >
          {saving ? (
            <>
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {t('payments.saving')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {isEditMode ? t('payments.updatePayment') : t('payments.savePayment')}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

